import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line
} from "recharts";
import {
  BarChart3,
  Map,
  Loader2,
  RefreshCw,
  Building2,
  AlertTriangle,
  CheckCircle,
  Clock,
  Wrench,
  Briefcase,
  Bath,
  Warehouse,
  Timer,
  Gauge,
  TrendingUp,
  TrendingDown,
  ClipboardList
} from "lucide-react";

const COLORS = ["#4a3fa8", "#655dc6", "#9b8fd4", "#b8aee0", "#E8D5F5"];
const STATUS_COLORS = { good: "#22c55e", warning: "#eab308", critical: "#ef4444" };

const TYPE_ICONS = {
  clinic: Building2,
  office: Briefcase,
  restroom: Bath,
  facility: Warehouse
};

const TYPE_LABELS = {
  clinic: "عيادة",
  office: "مكتب",
  restroom: "دورة مياه",
  facility: "مرفق"
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white p-3 rounded-lg shadow-lg border text-sm" dir="rtl">
      <p className="font-semibold text-slate-800 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, color = "#4a3fa8", testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}1a` }}>
        <Icon className="w-5.5 h-5.5" style={{ color, width: 22, height: 22 }} />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold leading-tight" style={{ color }}>{value}</p>
        <p className="text-xs font-medium text-slate-600">{label}</p>
        {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

const AdvancedDashboard = () => {
  const { token } = useAuth();
  const [charts, setCharts] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("charts");
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [hoveredLocation, setHoveredLocation] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [chartsRes, mapRes] = await Promise.all([
        axios.get(`${API}/statistics/charts`, { headers }),
        axios.get(`${API}/building-map`, { headers })
      ]);
      setCharts(chartsRes.data);
      setMapData(mapRes.data);
      
      const floors = Object.keys(mapRes.data.floors);
      if (floors.length > 0 && !selectedFloor) setSelectedFloor(floors[0]);
    } catch (err) {
      toast.error("خطأ في تحميل البيانات");
    } finally {
      setLoading(false);
    }
  }, [token, selectedFloor]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
      </div>
    );
  }

  const floors = mapData ? Object.keys(mapData.floors).sort() : [];
  const currentFloorLocations = mapData && selectedFloor ? mapData.floors[selectedFloor] || [] : [];

  return (
    <div className="space-y-6" dir="rtl" data-testid="advanced-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#4a3fa8]" data-testid="page-title">لوحة التحكم المتقدمة</h1>
          <p className="text-slate-500 text-sm mt-1">رسوم بيانية وخريطة تفاعلية للمبنى</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4" />
          تحديث
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} dir="rtl">
        <TabsList className="w-full justify-start bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="charts" className="gap-2 data-[state=active]:bg-[#4a3fa8] data-[state=active]:text-white" data-testid="tab-charts">
            <BarChart3 className="w-4 h-4" />
            الرسوم البيانية
          </TabsTrigger>
          <TabsTrigger value="map" className="gap-2 data-[state=active]:bg-[#4a3fa8] data-[state=active]:text-white" data-testid="tab-map">
            <Map className="w-4 h-4" />
            خريطة المبنى
          </TabsTrigger>
        </TabsList>

        {/* Charts Tab */}
        <TabsContent value="charts" className="mt-4 space-y-6">
          {charts && (
            <>
              {/* Maintenance KPIs */}
              {charts.kpis && (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="kpi-grid">
                  <KpiCard icon={ClipboardList} label="إجمالي الطلبات" value={charts.kpis.total} sub={`${charts.kpis.completed} مكتمل`} testId="kpi-total" />
                  <KpiCard icon={Gauge} label="نسبة الإنجاز الكلية" value={`${charts.kpis.completion_rate}%`} color={charts.kpis.completion_rate >= 80 ? "#16a34a" : charts.kpis.completion_rate >= 50 ? "#ca8a04" : "#dc2626"} testId="kpi-rate" />
                  <KpiCard icon={Wrench} label="طلبات مفتوحة الآن" value={charts.kpis.open_now} sub={charts.kpis.urgent_open > 0 ? `${charts.kpis.urgent_open} عاجل` : "لا يوجد عاجل"} color={charts.kpis.urgent_open > 0 ? "#dc2626" : "#ca8a04"} testId="kpi-open" />
                  <KpiCard icon={Timer} label="متوسط زمن الإغلاق" value={`${charts.kpis.avg_close_days} يوم`} testId="kpi-mttr" />
                  <KpiCard icon={Clock} label="الإغلاق خلال 48 ساعة" value={`${charts.kpis.pct_48h}%`} color={charts.kpis.pct_48h >= 70 ? "#16a34a" : "#ca8a04"} testId="kpi-48h" />
                  <KpiCard
                    icon={charts.kpis.month_delta !== null && charts.kpis.month_delta > 0 ? TrendingUp : TrendingDown}
                    label="طلبات هذا الشهر"
                    value={charts.kpis.this_month}
                    sub={charts.kpis.month_delta === null ? `الشهر الماضي: ${charts.kpis.last_month}` : `${charts.kpis.month_delta > 0 ? "+" : ""}${charts.kpis.month_delta}% عن الشهر الماضي (${charts.kpis.last_month})`}
                    color={charts.kpis.month_delta !== null && charts.kpis.month_delta > 0 ? "#dc2626" : "#16a34a"}
                    testId="kpi-month"
                  />
                </div>
              )}

              {/* Monthly Requests Chart */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#4a3fa8] mb-4" data-testid="monthly-chart-title">عدد الطلبات حسب الشهر</h3>
                  <div className="h-[300px]" dir="ltr">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={charts.monthly} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="total" name="إجمالي" fill="#4a3fa8" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="completed" name="مكتمل" fill="#22c55e" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Clinic Comparison */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-[#4a3fa8] mb-4" data-testid="clinic-chart-title">مقارنة العيادات (عدد المشاكل)</h3>
                    <div className="h-[300px]" dir="ltr">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={charts.clinics} layout="vertical" margin={{ top: 5, right: 30, left: 60, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis type="number" tick={{ fontSize: 12 }} />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="count" name="عدد الطلبات" fill="#655dc6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Status Distribution */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-[#4a3fa8] mb-4" data-testid="status-chart-title">توزيع حالات الطلبات</h3>
                    <div className="h-[300px]" dir="ltr">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={charts.status}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={5}
                            dataKey="value"
                            label={({ name, value }) => `${name} (${value})`}
                          >
                            {charts.status.map((_, index) => (
                              <Cell key={index} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Close time trend */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-[#4a3fa8] mb-4" data-testid="trend-chart-title">اتجاه متوسط زمن الإغلاق (أيام)</h3>
                    <div className="h-[280px]" dir="ltr">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={charts.close_trend} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Line type="monotone" dataKey="avg_days" name="متوسط الأيام" stroke="#4a3fa8" strokeWidth={3} dot={{ r: 5, fill: "#655dc6" }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Open requests aging */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-[#4a3fa8] mb-4" data-testid="aging-chart-title">عمر الطلبات المفتوحة</h3>
                    <div className="h-[280px]" dir="ltr">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={charts.aging} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="count" name="عدد الطلبات" radius={[4, 4, 0, 0]}>
                            {(charts.aging || []).map((_, i) => (
                              <Cell key={i} fill={["#22c55e", "#eab308", "#f97316", "#ef4444"][i]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Priority distribution */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-[#4a3fa8] mb-4" data-testid="priority-chart-title">توزيع الطلبات حسب الأولوية</h3>
                    <div className="h-[300px]" dir="ltr">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={charts.priorities || []}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={4}
                            dataKey="value"
                            label={({ name, value }) => `${name} (${value})`}
                          >
                            {(charts.priorities || []).map((p, i) => (
                              <Cell key={i} fill={{ urgent: "#ef4444", high: "#f97316", medium: "#eab308", low: "#22c55e" }[p.key] || "#655dc6"} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-wrap gap-2 justify-center mt-2">
                      {(charts.priorities || []).map((p, i) => (
                        <span key={i} className="text-[11px] text-slate-500 bg-slate-50 rounded-full px-2.5 py-1">
                          {p.name}: {p.rate}% مكتمل
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Top faulty devices */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-[#4a3fa8] mb-4" data-testid="devices-chart-title">الأجهزة الأكثر أعطالاً (Top 10)</h3>
                    {(charts.top_devices || []).length === 0 ? (
                      <p className="text-sm text-slate-400 py-10 text-center">لا توجد بلاغات مرتبطة بأجهزة بعد</p>
                    ) : (
                      <div className="h-[300px]" dir="ltr">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={charts.top_devices} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                            <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={110} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="count" name="عدد البلاغات" fill="#ef4444" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Completion Rate by Category */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#4a3fa8] mb-4" data-testid="category-chart-title">نسبة الإنجاز لكل فئة صيانة</h3>
                  <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                    {charts.categories.map((cat, i) => (
                      <div key={i} className="bg-slate-50 rounded-xl p-4 text-center">
                        <div className="relative w-20 h-20 mx-auto mb-3">
                          <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 36 36">
                            <path
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              fill="none"
                              stroke="#e5e7eb"
                              strokeWidth="3"
                            />
                            <path
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              fill="none"
                              stroke={cat.rate >= 80 ? "#22c55e" : cat.rate >= 50 ? "#eab308" : "#ef4444"}
                              strokeWidth="3"
                              strokeDasharray={`${cat.rate}, 100`}
                            />
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-slate-800">
                            {cat.rate}%
                          </span>
                        </div>
                        <p className="font-semibold text-slate-700">{cat.name}</p>
                        <p className="text-xs text-slate-500 mt-1">{cat.completed}/{cat.total} مكتمل</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Building Map Tab */}
        <TabsContent value="map" className="mt-4 space-y-4">
          {mapData && (
            <>
              {/* Legend */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-6 justify-center">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-green-500"></div>
                      <span className="text-sm text-slate-600">حالة جيدة</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                      <span className="text-sm text-slate-600">يوجد طلبات مفتوحة</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-red-500"></div>
                      <span className="text-sm text-slate-600">مشكلة عاجلة</span>
                    </div>
                    <Badge className="bg-[#4a3fa8] text-white hover:bg-[#4a3fa8] cursor-default">
                      إجمالي المواقع: {mapData.total_locations}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Floor Selector */}
              <div className="flex flex-wrap gap-2" data-testid="floor-selector">
                {floors.map(floor => {
                  const locs = mapData.floors[floor];
                  const hasIssues = locs.some(l => l.status !== "good");
                  return (
                    <button
                      key={floor}
                      onClick={() => setSelectedFloor(floor)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                        selectedFloor === floor
                          ? "bg-[#4a3fa8] text-white shadow-md"
                          : "bg-white text-slate-600 hover:bg-slate-100 border"
                      }`}
                      data-testid={`floor-${floor}`}
                    >
                      <Building2 className="w-4 h-4" />
                      <span>{floor}</span>
                      {hasIssues && <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse"></span>}
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        selectedFloor === floor ? "bg-white/20" : "bg-slate-100"
                      }`}>{locs.length}</span>
                    </button>
                  );
                })}
              </div>

              {/* Floor Plan */}
              <Card className="overflow-hidden">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#4a3fa8] mb-4" data-testid="floor-title">
                    مخطط {selectedFloor}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="floor-grid">
                    {currentFloorLocations.map(loc => {
                      const TypeIcon = TYPE_ICONS[loc.type] || Building2;
                      const bgColor = loc.status === "critical" 
                        ? "bg-red-50 border-red-300 hover:bg-red-100" 
                        : loc.status === "warning" 
                        ? "bg-yellow-50 border-yellow-300 hover:bg-yellow-100" 
                        : "bg-green-50 border-green-300 hover:bg-green-100";
                      const dotColor = STATUS_COLORS[loc.status];
                      
                      return (
                        <div
                          key={loc.id}
                          className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${bgColor} ${
                            hoveredLocation === loc.id ? "shadow-lg scale-105" : ""
                          }`}
                          onMouseEnter={() => setHoveredLocation(loc.id)}
                          onMouseLeave={() => setHoveredLocation(null)}
                          data-testid={`location-${loc.id}`}
                        >
                          {/* Status dot */}
                          <div
                            className="absolute top-2 left-2 w-3 h-3 rounded-full"
                            style={{ backgroundColor: dotColor }}
                          ></div>
                          
                          <div className="text-center">
                            <TypeIcon className="w-8 h-8 mx-auto mb-2" style={{ color: dotColor }} />
                            <p className="font-semibold text-sm text-slate-800 leading-tight">{loc.name}</p>
                            <p className="text-xs text-slate-500 mt-1">{TYPE_LABELS[loc.type]}</p>
                            
                            {loc.open_requests > 0 && (
                              <div className="flex items-center justify-center gap-1 mt-2">
                                <Wrench className="w-3 h-3 text-slate-500" />
                                <span className="text-xs font-medium text-slate-600">{loc.open_requests} طلب</span>
                              </div>
                            )}
                            {loc.urgent_requests > 0 && (
                              <div className="flex items-center justify-center gap-1 mt-1">
                                <AlertTriangle className="w-3 h-3 text-red-500" />
                                <span className="text-xs font-medium text-red-600">{loc.urgent_requests} عاجل</span>
                              </div>
                            )}
                            {loc.status === "good" && (
                              <div className="flex items-center justify-center gap-1 mt-2">
                                <CheckCircle className="w-3 h-3 text-green-500" />
                                <span className="text-xs text-green-600">جيدة</span>
                              </div>
                            )}
                          </div>

                          {/* Hover tooltip */}
                          {hoveredLocation === loc.id && (
                            <div className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full bg-slate-800 text-white text-xs rounded-lg px-3 py-2 z-10 whitespace-nowrap shadow-xl">
                              <p className="font-semibold">{loc.name}</p>
                              <p>{loc.open_requests} طلب مفتوح • {loc.urgent_requests} عاجل</p>
                              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-slate-800 rotate-45"></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Floor Summary */}
                  <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      جيدة: {currentFloorLocations.filter(l => l.status === "good").length}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-yellow-500" />
                      طلبات مفتوحة: {currentFloorLocations.filter(l => l.status === "warning").length}
                    </span>
                    <span className="flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      عاجلة: {currentFloorLocations.filter(l => l.status === "critical").length}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdvancedDashboard;
